import {
  assign,
  Interpreter,
  createMachine,
  MachineConfig,
  send,
  sendParent,
  Sender,
} from 'xstate';
import {escalate} from 'xstate/lib/actions';
import * as R from 'ramda';
import {
  ApiClient,
  ApiError,
  ApiErrorKind,
  UnknownServerData,
} from '@practicegenius/http-clients-js/src/types';
import {makeNilApiClient} from '@practicegenius/http-clients-js/src/ApiClient';
import {objectKeysToCamelCase} from '@practicegenius/pg-util/src/ObjectUtils';
import {IMT} from '@practicegenius/pg-util/src/StringUtils';
import * as sharedMachineOptions from './sharedMachineOptions';
import {
  Event as ParentEvent,
  EventSchema as ParentEventSchema,
} from './machineTypes';
import {transformRegistration} from '../data/transformers';
import lang from '../lang';
import {RegistrationData} from '../data/types';

enum State {
  init = 'init',
  verifyingName = 'verifyingName',
  committingRewardsCard = 'committingRewardsCard',
  committingInviteCode = 'committingInviteCode',
  done = 'done',
}

type Context = {
  apiClient: ApiClient;
  token: RegistrationData['token'];
  firstname: RegistrationData['account']['firstname'];
  lastname: RegistrationData['account']['lastname'];
  cardNumber: RegistrationData['cardNumber'];
  inviteCode: RegistrationData['inviteCode'];
};

type StateSchema = {
  states: {
    init: {};
    verifyingName: {};
    committingRewardsCard: {};
    committingInviteCode: {};
    done: {};
  };
};

enum Event {
  VERIFY_NAME = 'VERIFY_NAME',
}

type EventSchema =
  | ParentEventSchema
  | {type: Event.VERIFY_NAME; payload: {firstname: string; lastname: string}};

const defaultContext: Context = {
  apiClient: makeNilApiClient(),
  token: null,
  firstname: null,
  lastname: null,
  cardNumber: null,
  inviteCode: null,
};

const makeUserConnectionCommittingStateConfig = (serviceName: string) => ({
  invoke: {
    id: serviceName,
    src: serviceName,
    onError: {
      actions: escalate((context, event) => event),
    },
  },
  on: {
    [ParentEvent.UPDATE_DATA]: {
      target: State.done,
      actions: 'bubbleEventToParent',
    },
    // Handles ParentEvent.HAS_VALIDATION_ERRORS & ParentEvent.NAME_NOT_MATCHED & ParentEvent.UNHANDLED_ERROR
    '*': {
      actions: 'bubbleEventToParent',
    },
  },
});

const id = 'nameVerification';
const config: MachineConfig<Context, StateSchema, EventSchema> = {
  id,
  initial: State.init,
  context: defaultContext,

  // @ts-ignore - XState types are very unhappy about things in here
  states: {
    [State.init]: {
      // This seems extraneous, but is necessary to setup the proper event/payload for the shared updateRegistration service.
      entry: 'verifyName',

      on: {
        [Event.VERIFY_NAME]: State.verifyingName,
      },
    },

    [State.verifyingName]: {
      invoke: {
        id: 'update-registration',
        src: 'updateRegistration',
        onError: {
          actions: escalate((context, event) => event),
        },
      },
      on: {
        [ParentEvent.UPDATE_DATA]: [
          {
            target: State.committingRewardsCard,
            cond: 'hasRewardsCard',
            actions: ['bubbleEventToParent', 'setLocalToken'],
          },
          {
            target: State.committingInviteCode,
            cond: 'hasInviteCode',
            actions: ['bubbleEventToParent', 'setLocalToken'],
          },
          {target: State.done, actions: 'bubbleEventToParent'},
        ],
        // handles ParentEvent.HAS_VALIDATION_ERRORS & ParentEvent.SKIP_TO_ALREADY_REGISTERED
        '*': {
          actions: 'bubbleEventToParent',
        },
      },
    },
    [State.committingRewardsCard]:
      makeUserConnectionCommittingStateConfig('commitRewardsCard'),
    [State.committingInviteCode]:
      makeUserConnectionCommittingStateConfig('commitInviteCode'),
    [State.done]: {
      type: 'final',
    },
  },
};

const makeUserConnectionSuccessHandler =
  (send: Sender<EventSchema>) => (data: UnknownServerData) => {
    send({
      type: ParentEvent.UPDATE_DATA,
      payload: transformRegistration(data),
    });
  };

const makeUserConnectionErrorHandler =
  (send: Sender<EventSchema>, genericErrorMessage: string) =>
  (error: ApiError) => {
    if (error.kind === ApiErrorKind.ValidationError) {
      const errors = objectKeysToCamelCase(error.errors);

      if ('nameVerification' in errors) {
        return send(ParentEvent.NAME_NOT_MATCHED);
      }

      send({
        type: ParentEvent.HAS_VALIDATION_ERRORS,
        errors,
      });
    }

    send({
      type: ParentEvent.UNHANDLED_ERROR,
      message: IMT(lang.general.registrationError.message, {
        detailedError: genericErrorMessage,
      }),
      error,
    });
  };

// @ts-ignore - I can't get config to play nice with types. I think it has something to do with the shared updateRegistration service
const machine = createMachine(config, {
  guards: {
    hasRewardsCard: (context: Context) => Boolean(context.cardNumber),
    hasInviteCode: (context: Context) => Boolean(context.inviteCode),
  },

  actions: {
    verifyName: send(context => ({
      type: Event.VERIFY_NAME,
      payload: {firstname: context.firstname, lastname: context.lastname},
    })),

    // @ts-ignore - Cannot get these types to play well
    bubbleEventToParent: sendParent<Context, EventSchema>(
      (context: Context, event: EventSchema) => event
    ),

    setLocalToken: assign((context, event) =>
      event.type !== ParentEvent.UPDATE_DATA
        ? context
        : R.assoc('token', event.payload.token, context)
    ),
  },

  services: {
    // @ts-ignore - Cannot get these types to play well
    updateRegistration: sharedMachineOptions.services.updateRegistration,

    commitRewardsCard:
      (context: Context) => async (send: Sender<EventSchema>) => {
        // When verifying the name, we also need to finish connecting the rewards number after a correct name verification
        (
          await context.apiClient.post(
            '/global_users/registration/rewards_card?include=practice_records',
            {
              card_number: context.cardNumber,
              firstname: context.firstname,
              lastname: context.lastname,
              token: context.token,
            }
          )
        )
          .mapData(makeUserConnectionSuccessHandler(send))
          .mapErr(
            makeUserConnectionErrorHandler(
              send,
              lang.general.registrationError.unableToCommitRewardsCard
            )
          );
      },

    commitInviteCode:
      (context: Context) => async (send: Sender<EventSchema>) => {
        (
          await context.apiClient.post(
            '/global_users/registration/invite_code?include=practice_records',
            {
              invite_code: context.inviteCode,
              firstname: context.firstname,
              lastname: context.lastname,
              token: context.token,
            }
          )
        )
          .mapData(makeUserConnectionSuccessHandler(send))
          .mapErr(
            makeUserConnectionErrorHandler(
              send,
              lang.general.registrationError.unableToCommitInviteCode
            )
          );
      },
  },
});

export type ActorType = Interpreter<Context, StateSchema, EventSchema>;
export {State, defaultContext, machine, id};
